import React, { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChevronLeft,
  ChevronRight,
  Building2,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  X
} from "lucide-react";

import { useForm } from "react-hook-form";
import { z } from "zod";
import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import useToast from "../hooks/useToast";
import {
  createDepartment,
  deleteDepartment,
  getDepartments,
  updateDepartment
} from "../services/departmentService";

const departmentSchema = z.object({
  name: z.string().trim().min(1, "Name is required")
});

const defaultValues = {
  name: ""
};

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, "...", totalPages];
  }

  if (currentPage >= totalPages - 3) {
    return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

export default function Departments() {
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeletingDepartment, setIsDeletingDepartment] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDepartmentId, setEditingDepartmentId] = useState(null);
  const [departmentPendingDelete, setDepartmentPendingDelete] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });

  const { showErrorToast, showSuccessToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues,
    resolver: zodResolver(departmentSchema)
  });

  const refreshListing = (targetPage = pagination.page, targetLimit = pagination.limit) => {
    if (targetPage !== pagination.page || targetLimit !== pagination.limit) {
      setPagination((current) => ({
        ...current,
        page: targetPage,
        limit: targetLimit
      }));
      return;
    }

    loadDepartments(targetPage, targetLimit);
  };

  async function loadDepartments(pageArg = pagination.page, limitArg = pagination.limit) {
    setIsLoading(true);

    const response = await getDepartments({
      page: pageArg,
      limit: limitArg
    });

    if (response?.success) {
      setDepartments(response.data.departments || []);
      setPagination({
        page: response.data.pagination?.page || pageArg,
        limit: response.data.pagination?.limit || limitArg,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 1
      });
    } else {
      showErrorToast(response?.message || "Unable to load departments");
    }

    setIsLoading(false);
  }

  useEffect(() => {
    loadDepartments(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  const openCreateModal = () => {
    reset(defaultValues);
    setEditingDepartmentId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    reset(defaultValues);
    setEditingDepartmentId(null);
    setIsModalOpen(false);
  };

  const openDeleteModal = (department) => {
    setDepartmentPendingDelete(department);
  };

  const closeDeleteModal = () => {
    if (isDeletingDepartment) {
      return;
    }

    setDepartmentPendingDelete(null);
  };

  const handleEdit = (department) => {
    setEditingDepartmentId(department.id);
    reset({
      name: department.name
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!departmentPendingDelete) {
      return;
    }

    setIsDeletingDepartment(true);

    const response = await deleteDepartment(departmentPendingDelete.id);

    setIsDeletingDepartment(false);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to delete department");
      return;
    }

    const nextPage = departments.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
    showSuccessToast(response.message || "Department deleted successfully");
    closeDeleteModal();
    refreshListing(nextPage, pagination.limit);
  };

  const onSubmit = async (values) => {
    const response = editingDepartmentId
      ? await updateDepartment(editingDepartmentId, values)
      : await createDepartment(values);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to save department");
      return;
    }

    showSuccessToast(response.message || "Department saved successfully");
    closeModal();

    if (editingDepartmentId) {
      refreshListing(pagination.page, pagination.limit);
      return;
    }

    refreshListing(1, pagination.limit);
  };

  const startItem = pagination.total ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const endItem = pagination.total ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;
  const pageNumbers = getVisiblePageNumbers(pagination.page, pagination.totalPages);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Management"
        title="Departments"
        description="Manage hospital departments with ease."
        badge={`${pagination.total} Departments`}
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Departments List</CardTitle>
            <p className="mt-2 text-sm text-panel-muted">
              Add, edit, or delete departments available in the system.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" onClick={openCreateModal}>
              <Plus className="mr-2 h-4 w-4" />
              Add Department
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="rounded-3xl border border-slate-100 bg-slate-50/80 p-6 text-sm text-panel-muted">
              Loading departments...
            </div>
          ) : departments.length ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-100">
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead className="bg-slate-50/90">
                      <tr className="text-left">
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">ID</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Name</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Created At</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted text-right">Actions</th>
                      </tr>
                    </thead>

                    <tbody className="bg-white">
                      {departments.map((dept) => (
                        <tr key={dept.id} className="border-t border-slate-100 align-middle">
                          <td className="px-5 py-4 text-sm text-panel-muted">#{dept.id}</td>
                          <td className="px-5 py-4 text-sm font-semibold text-panel-ink">{dept.name}</td>
                          <td className="px-5 py-4 text-sm text-panel-muted">
                            {new Date(dept.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex justify-end gap-2">
                              <Button type="button" size="sm" variant="outline" onClick={() => handleEdit(dept)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="hover:bg-rose-50 hover:text-rose-600 text-panel-muted"
                                onClick={() => openDeleteModal(dept)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-3xl border border-slate-100 bg-slate-50/70 px-4 py-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-panel-muted">
                  Showing <span className="font-semibold text-panel-ink">{startItem}</span> to{" "}
                  <span className="font-semibold text-panel-ink">{endItem}</span> of{" "}
                  <span className="font-semibold text-panel-ink">{pagination.total}</span> departments
                </p>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={pagination.page <= 1}
                    onClick={() => refreshListing(pagination.page - 1)}
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" />
                    Prev
                  </Button>
                  <div className="flex flex-wrap items-center gap-2">
                    {pageNumbers.map((item, index) =>
                      item === "..." ? (
                        <span key={`ellipsis-${index}`} className="px-1 text-sm font-medium text-panel-muted">
                          ...
                        </span>
                      ) : (
                        <Button
                          key={item}
                          type="button"
                          size="sm"
                          variant={pagination.page === item ? "default" : "outline"}
                          onClick={() => refreshListing(item)}
                        >
                          {item}
                        </Button>
                      )
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => refreshListing(pagination.page + 1)}
                  >
                    Next
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-slate-50/80 p-8 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Building2 className="h-6 w-6" />
              </div>
              <p className="mt-4 text-lg font-semibold text-panel-ink">No departments found</p>
              <p className="mt-2 text-sm text-panel-muted">
                Add your first department to get started.
              </p>
              <Button type="button" className="mt-5" onClick={openCreateModal}>
                <Plus className="mr-2 h-4 w-4" />
                Add Department
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {isModalOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4">
          <div className="w-full max-w-md rounded-[32px] border border-white/80 bg-white shadow-[0_24px_80px_rgba(44,33,111,0.22)]">
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
              <h2 className="text-xl font-bold text-panel-ink">
                {editingDepartmentId ? "Edit Department" : "Add Department"}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-panel-muted transition-colors hover:bg-slate-100 hover:text-panel-ink"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form className="p-6 space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-2">
                <FieldLabel>Department Name</FieldLabel>
                <Input placeholder="e.g. Cardiology, Orthopedics" {...register("name")} />
                {errors.name ? <FieldError>{errors.name.message}</FieldError> : null}
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1" disabled={isSubmitting}>
                  {isSubmitting ? "Saving..." : editingDepartmentId ? "Update" : "Save"}
                </Button>
                <Button type="button" variant="outline" className="flex-1" onClick={closeModal}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {departmentPendingDelete ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-[32px] border border-white/80 bg-white p-6 shadow-[0_24px_80px_rgba(44,33,111,0.22)]">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-600">
              <Trash2 className="h-5 w-5" />
            </div>
            <h2 className="mt-5 text-2xl font-bold text-panel-ink">Delete Department</h2>
            <p className="mt-3 text-sm leading-6 text-panel-muted">
              Are you sure you want to delete <span className="font-semibold text-panel-ink">{departmentPendingDelete.name}</span>?
              This action cannot be undone.
            </p>

            <div className="mt-6 flex gap-3">
              <Button type="button" variant="destructive" className="flex-1" onClick={handleDelete} disabled={isDeletingDepartment}>
                {isDeletingDepartment ? "Deleting..." : "Delete"}
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={closeDeleteModal} disabled={isDeletingDepartment}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
